import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Button, ConfirmDialog, DataGrid, Modal, StatusBadge, useToast,
  type DataGridColumn,
} from '@kss/ui';
import { Plus, RefreshCw, Sheet, Trash2, Pencil } from 'lucide-react';
import { encodeId } from '../../utils/idCodec';
import { AddDropdown } from '../shared/AddDropdown';
import { useCsvIO } from '../shared/useCsvIO';
import type { CsvColumn } from '../shared/csv';
import type { Store, StoreCreate } from '@kss/api';
import { storeService } from '@kss/api';
import { NameCell } from '../../hooks/useNameMaps';
import { useModulePerms } from '../../hooks/useModulePerms';

const CSV_COLUMNS: CsvColumn<Store>[] = [
  { header: 'name', get: (r) => r.name },
  { header: 'code', get: (r) => r.code ?? '' },
  { header: 'address', get: (r) => r.address ?? '' },
  { header: 'isActive', get: (r) => (r.isActive ? 'true' : 'false') },
  { header: 'companyId', get: (r) => r.companyId ?? '' },
];

function csvRowToCreate(row: Record<string, string>): StoreCreate | null {
  const name = (row.name ?? '').trim();
  if (!name) return null;
  const active = (row.isActive ?? '').trim().toLowerCase();
  const isFalse = ['0', 'false', 'no', 'n', 'inactive'].includes(active);
  return {
    name,
    code: row.code?.trim() || undefined,
    address: row.address?.trim() || undefined,
    companyId: row.companyId?.trim() || undefined,
    isActive: !isFalse,
  };
}

export default function Stores() {
  const navigate = useNavigate();
  const toast = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;

  const [rows, setRows] = useState<Store[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<(string | number)[]>([]);
  const [detailRow, setDetailRow] = useState<Store | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Store | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const data = await storeService.all();
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to load stores';
      setError(msg); toastRef.current.error('Load failed', msg);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const perms = useModulePerms('Store');
  const csv = useCsvIO<Store, StoreCreate>({
    filename: 'stores',
    columns: CSV_COLUMNS,
    rows,
    selected,
    rowId: (r) => r.storeId,
    rowToCreate: csvRowToCreate,
    bulkCreate: (items) => storeService.create(items),
    reload: load,
    noun: 'store',
    module: 'Store',
  });

  const handleDelete = async () => {
    if (!confirmDelete?.storeId) return;
    setDeleting(true);
    try {
      await storeService.delete(confirmDelete.storeId);
      toast.success('Deleted', `${confirmDelete.name} removed`);
      setConfirmDelete(null); await load();
    } catch (e) { toast.error('Delete failed', e instanceof Error ? e.message : 'Failed'); }
    finally { setDeleting(false); }
  };

  const columns: DataGridColumn<Store>[] = [
    { accessor: 'code', title: 'Code', sortable: true, filterable: true, width: 120 },
    { accessor: 'name', title: 'Name', sortable: true, filterable: true },
    {
      // Live API ships the address as four discrete columns
      // (`addressLine1` + `city` + `state` + `postalCode`) — there
      // is no `address` field, so the old binding always rendered
      // an em-dash. Compose the parts at render-time instead.
      accessor: 'addressLine1',
      title: 'Address',
      sortable: true,
      render: (r) => {
        const r2 = r as Store & {
          addressLine1?: string;
          addressLine2?: string;
          city?: string;
          state?: string;
          postalCode?: string;
        };
        const parts = [r2.addressLine1, r2.city, r2.state, r2.postalCode].filter(
          (s) => !!s && String(s).trim().length > 0,
        );
        return parts.length ? parts.join(', ') : '—';
      },
    },
    { accessor: 'isActive', title: 'Status', sortable: true,
      render: (r) => <StatusBadge status={r.isActive ? 'active' : 'inactive'} /> },
    { accessor: 'storeId', title: 'Actions', width: 110,
      render: (r) => (
        <div className="flex items-center gap-1">
          {perms.canUpdate && (
            <button type="button" className="inline-flex h-7 w-7 items-center justify-center rounded text-content-secondary hover:bg-surface-card-hover"
              onClick={(e) => { e.stopPropagation(); if (r.storeId) navigate(`/panel/stores/${encodeId(r.storeId)}`); }} title="Edit">
              <Pencil className="h-3.5 w-3.5" />
            </button>
          )}
          {perms.canDelete && (
            <button type="button" className="inline-flex h-7 w-7 items-center justify-center rounded text-danger hover:bg-surface-card-hover"
              onClick={(e) => { e.stopPropagation(); setConfirmDelete(r); }} title="Delete">
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="-m-6 p-[3px]">
      {error && <Alert variant="danger" className="mb-3">{error}</Alert>}
      {csv.fileInput}
      <DataGrid<Store>
        title={<span className="hidden sm:inline">All stores</span>}
        columnPicker onRowClick={(row) => setDetailRow(row)} data={rows} columns={columns}
        selectable selectedIds={selected} onSelectedChange={setSelected}
        defaultPageSize={10} pageSizes={[5, 10, 25, 50, 100]}
        toolbar={
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" onClick={load} disabled={loading}>
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
            <AddDropdown
              items={[
                ...(perms.canCreate
                  ? [{ label: 'New store', to: '/panel/stores/new', icon: <Plus className="h-3.5 w-3.5" /> }]
                  : []),
                ...(perms.canImport
                  ? [{ label: 'Bulk import (spreadsheet)', to: '/panel/stores/bulk', icon: <Sheet className="h-3.5 w-3.5" /> }]
                  : []),
                ...csv.menuItems,
              ]}
            />
          </div>
        }
      />
      <Modal open={!!detailRow} onClose={() => setDetailRow(null)} title={detailRow ? `Store · ${detailRow.name}` : ''} width={520}
        footer={<>
          <Button variant="outline" onClick={() => setDetailRow(null)}>Close</Button>
          {detailRow?.storeId && (
            <Button onClick={() => { const id = detailRow.storeId!; setDetailRow(null); navigate(`/panel/stores/${encodeId(id)}`); }}>Edit</Button>
          )}
        </>}>
        {detailRow && (
          <div className="grid gap-3 rounded-md border border-border bg-surface-card-hover p-4 text-sm">
            <DetailRow label="Code" value={detailRow.code} />
            <DetailRow label="Name" value={detailRow.name} />
            <DetailRow label="Address" value={detailRow.address} />
            <div className="grid grid-cols-2 gap-3">
              <DetailRow label="Status" value={<StatusBadge status={detailRow.isActive ? 'active' : 'inactive'} />} />
              {/* Resolve `companyId` → company name via NameCell so the
                  detail modal mirrors what the grid already does — no
                  raw GUIDs surfaced to admins. */}
              <DetailRow label="Company" value={<NameCell kind="company" id={detailRow.companyId} />} />
            </div>
          </div>
        )}
      </Modal>
      <ConfirmDialog isOpen={!!confirmDelete} onClose={() => (deleting ? undefined : setConfirmDelete(null))}
        onConfirm={handleDelete} title="Delete store"
        message={confirmDelete ? `This will permanently remove "${confirmDelete.name}". This action cannot be undone.` : ''}
        confirmLabel={deleting ? 'Deleting…' : 'Delete'} variant="danger" />
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded border border-border bg-surface-card px-3 py-2">
      <span className="text-xs text-content-muted">{label}</span>
      <span className="text-sm font-medium text-content-primary">{value || <em className="text-content-muted">—</em>}</span>
    </div>
  );
}
